// @flow strict
import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import type {EntityType} from 'src/types/ats-entities';
import {useApiQuery} from 'src/hooks/useApiQuery';
import {useHistory, Link} from 'src/rerouter';
import matchSorter from 'match-sorter';
import {classify} from 'src/utils/classify';
import {MultiPanePanel} from 'src/components/lib/multi-pane-panel';

import {
  PanelBody,
  PanelHeader,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  SubTitleLarge,
  SubTitleSmall,
  BodyMedium,
  FormLabelMedium,
  SubTitleExtraSmall,
  BodyLarge,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  BasicSingleCell,
  TableBottomBar,
  TableTopBar,
  Table,
} from '@spaced-out/ui-design-system/lib/components/Table';
import {CircularLoader as Loading} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {Card} from '@spaced-out/ui-design-system/lib/components/Card';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {DebouncedSearchInput} from 'src/components/lib/genesis/debounced-search-input.jsx';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {Pagination} from '@spaced-out/ui-design-system/lib/components/Pagination';
// $FlowFixMe[nonstrict-import]
import {AutoTruncatedTextGenesis as AutoTruncatedText} from 'src/components/lib/truncated-text/truncated-text.jsx';
import {LabelValue} from './module-addon-common.jsx';
import {HorizontalSeparator} from 'src/components/lib/horizontal-separator/horizontal-separator.jsx';

import css from './survey-addon.css';


export const SURVEY_ADDON_ID = 'sense-survey-row';
export const SURVEY_LINK_TEXT = 'link to survey';

type SurveyEntry = {
  id: string,
  name: string,
  status: string,
  entity_type: EntityType,
};

const Name = ({data, extras}) => {
  const isSelected = extras?.surveyId === data.id;
  return (
    <BasicSingleCell className={classify({[css.highlight]: isSelected})}>
      <BodyMedium className={css.name}>
        <AutoTruncatedText className={css.name} text={data.name} />
      </BodyMedium>
    </BasicSingleCell>
  );
};

const Selection = ({data, extras}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const isSelected = extras?.surveyId === data.id;
  return (
    <BasicSingleCell
      className={classify(css.addonActions, {[css.highlight]: isSelected})}
    >
      <Link
        to={`/automation-survey/${data.id}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Button size="small" type="ghost">
          {t('Preview', 'Preview')}
        </Button>
      </Link>
      <Button
        onClick={() => {
          extras?.onSelect(data.id);
        }}
        size="small"
        type="secondary"
        iconRightName="chevron-right"
      >
        {t('Select', 'Select')}
      </Button>
    </BasicSingleCell>
  );
};

export const SurveyAddon = ({
  defaultType = 'button',
  initialPanel = 'component',
  workflowEntityType,
  onClose,
  onSubmit,
  loadingSurveySubmit,
  editSurveyInfo,
}: {
  onClose: () => void,
  onSubmit: (surveyId: string, isPreview: boolean, widgetType: ?string) => void,
  defaultType?: string,
  initialPanel?: string,
  workflowEntityType: EntityType,
  loadingSurveySubmit: boolean,
  editSurveyInfo: {
    surveyId: ?number,
    surveyType: ?string,
  },
}): React.Node => {
  const {data: result, isLoading} = useApiQuery(
    {pathname: 'survey/schema', query: {onlyAttachable: 'true'}},
    {
      apiOptions: {
        options: {
          apiPath: '/bff/api/v1/',
        },
      },
    },
  );
  const entries: Array<SurveyEntry> =
    result?.map(({id, name, status, entity_type}) => ({
      id,
      name,
      status,
      entity_type,
    })) ?? [];
  const filteredSurveysEntries = entries.filter(
    (entry) => entry.entity_type === workflowEntityType,
  );
  const isEdit =
    editSurveyInfo.surveyType != null && editSurveyInfo.surveyId != null;
  const [panel, setPanel] = React.useState(() => {
    if (isEdit) {
      return 'details';
    }
    return initialPanel;
  });

  const [widgetType, setWidgetType] = React.useState(defaultType);

  const handleComponentTypeSelect = (type: string) => {
    if (isEdit && editSurveyInfo.surveyId) {
      const selectedSurvey = filteredSurveysEntries.find(
        ({id}) => editSurveyInfo.surveyId === id,
      );
      if (selectedSurvey) {
        onSubmit(selectedSurvey.id, isEdit, type);
      }
    } else {
      setWidgetType(type);
      setPanel('surveys');
    }
  };

  const handleSubmit = (surveyId) => {
    if (isEdit) {
      onSubmit(surveyId, isEdit, editSurveyInfo.surveyType);
    } else {
      onSubmit(surveyId, false, widgetType);
    }
  };

  const handleBack = () => {
    if (isEdit) {
      setPanel('details');
    } else {
      setPanel('component');
    }
  };

  const handleBackWidget = () => {
    setPanel('details');
  };

  const handleDetailsSelect = (panelType) => {
    setPanel(panelType);
  };

  const getChildren = (currentPanel) => {
    if (
      currentPanel === 'details' &&
      editSurveyInfo.surveyId != null &&
      editSurveyInfo.surveyType
    ) {
      return (
        <SurveyDetails
          surveyId={editSurveyInfo.surveyId}
          surveyType={editSurveyInfo.surveyType}
          onSelect={handleDetailsSelect}
          surveyEntries={filteredSurveysEntries}
          surveysLoading={isLoading}
          closePanel={onClose}
        />
      );
    }
    if (currentPanel === 'component') {
      return (
        <WidgetConfig
          onSelect={handleComponentTypeSelect}
          onBack={isEdit ? handleBackWidget : null}
          loadingSurveySubmit={loadingSurveySubmit}
        />
      );
    }
    if (currentPanel === 'surveys') {
      return (
        <SurveySelection
          onSubmit={handleSubmit}
          onBack={handleBack}
          surveyId={editSurveyInfo.surveyId}
          loading={isLoading}
          surveyEntries={filteredSurveysEntries}
          loadingSurveySubmit={loadingSurveySubmit}
        />
      );
    }
    return null;
  };

  return (
    <MultiPanePanel
      onClose={onClose}
      isOpen={true}
      hideBackdrop={false}
      classNames={{backdrop: css.panelBackdrop}}
    >
      {getChildren(panel)}
    </MultiPanePanel>
  );
};

export const SurveyDetails = ({
  surveyId,
  surveyType,
  onSelect,
  surveyEntries,
  surveysLoading,
  closePanel,
}: {
  surveyId: number,
  surveyType: string,
  onSelect: (string) => mixed,
  surveyEntries: Array<SurveyEntry>,
  surveysLoading: boolean,
  closePanel: () => mixed,
}): React.Node => {
  const router = useHistory();
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const surveyObject = surveyEntries.find(({id}) => id === surveyId);
  const surveyName = surveyObject?.name ?? '';
  return (
    <>
      <PanelHeader onCloseButtonClick={closePanel}>
        <SubTitleLarge>
          {t('Survey_AddOn_Configuration', 'Survey Add-On Configuration')}
        </SubTitleLarge>
      </PanelHeader>
      {surveysLoading ? (
        <PanelBody className={css.loaderContainer}>
          <Loading size="large" />
        </PanelBody>
      ) : (
        <PanelBody>
          <Card
            paddingTop="medium"
            paddingBottom="medium"
            paddingLeft="small"
            paddingRight="small"
            classNames={{wrapper: css.detailsCard}}
          >
            <div className={css.detailsItems}>
              <LabelValue
                label={t('Survey_Name', 'Survey Name')}
                value={surveyName}
              />
              <div>
                <Link
                  to={`/automation-survey/${surveyId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button type="ghost">{t('Preview', 'Preview')}</Button>
                </Link>
                <Button
                  type="secondary"
                  iconRightName="chevron-right"
                  onClick={() => onSelect('surveys')}
                >
                  {t('Edit', 'Edit')}
                </Button>
              </div>
            </div>
            <HorizontalSeparator />
            <div className={css.detailsItems}>
              <LabelValue
                label={t('Display_As', 'Display As')}
                value={
                  surveyType === 'text'
                    ? t('Text_Component', 'Text Component')
                    : t('Button', 'Button')
                }
              />
              <Button
                type="secondary"
                iconRightName="chevron-right"
                onClick={() => onSelect('component')}
              >
                {t('Edit', 'Edit')}
              </Button>
            </div>
          </Card>
        </PanelBody>
      )}
    </>
  );
};

export const WidgetConfig = ({
  onSelect,
  closePanel,
  onBack,
  loadingSurveySubmit,
}: {
  onSelect: (string) => mixed,
  closePanel?: () => mixed,
  onBack: ?() => mixed,
  loadingSurveySubmit: boolean,
}): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  return (
    <>
      <PanelHeader onCloseButtonClick={closePanel}>
        <>
          {onBack && (
            <Button onClick={onBack} type="ghost" iconLeftName="chevron-left" />
          )}
          <SubTitleLarge>
            {t('Select_Display_Type', 'Select Display Type')}
          </SubTitleLarge>
        </>
      </PanelHeader>
      <PanelBody>
        {loadingSurveySubmit ? (
          <div className={css.loaderContainer}>
            <Loading />
            <BodyLarge>
              {t(
                'Changing_the_display_type_for_the_attached_survey',
                'Changing the display type for the attached survey',
              )}
            </BodyLarge>
          </div>
        ) : (
          <div className={css.content}>
            <SubTitleSmall>
              {t(
                'Select_the_component_to_open_survey_link',
                'Select the component to open survey link',
              )}
            </SubTitleSmall>
            <ComponentTypeItem
              iconName="rectangle-wide"
              id="button"
              onSelect={onSelect}
              title={t('Button', 'Button')}
              description={t(
                'Survey_will_be_opened_in_new_tab_when_button_is_clicked',
                'Survey will be opened in new tab when button is clicked',
              )}
            />
            <ComponentTypeItem
              iconName="text"
              id="text"
              onSelect={onSelect}
              title={t('Text_Component', 'Text Component')}
              description={t(
                'Survey_will_be_opened_in_new_tab_when_hyper_link_is_clicked',
                'Survey will be opened in new tab when hyper link is clicked',
              )}
            />
          </div>
        )}
      </PanelBody>
    </>
  );
};

//note(vish): this is a copy of the chatbot-addon.jsx. Be sure to extract to a common component if you make any changes
export const ComponentTypeItem = ({
  iconName,
  onSelect,
  id,
  title,
  description,
}: {
  iconName: string,
  onSelect: (string) => mixed,
  id: string,
  title: React.Node,
  description: React.Node,
}): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  return (
    <Card
      paddingTop="small"
      paddingBottom="small"
      paddingLeft="small"
      paddingRight="small"
      classNames={{wrapper: css.componentTypeItemContainer}}
    >
      <div className={css.iconContainer}>
        <Icon name={iconName} />
      </div>

      <div>
        <FormLabelMedium>{title}</FormLabelMedium>
        <BodyMedium>{description}</BodyMedium>
      </div>
      <Button
        type="secondary"
        onClick={() => {
          onSelect(id);
        }}
        iconRightName="chevron-right"
      >
        {t('Select', 'Select')}
      </Button>
    </Card>
  );
};

export const SurveySelection = ({
  onSubmit,
  onBack,
  surveyEntries,
  loadingSurveySubmit,
  closePanel,
  loading,
  surveyId,
}: {
  onSubmit: (surveyId: string) => mixed,
  onBack: () => mixed,
  surveyEntries: Array<SurveyEntry>,
  loadingSurveySubmit: boolean,
  closePanel?: () => mixed,
  loading: boolean,
  surveyId?: ?number,
}): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [query, setQuery] = React.useState('');
  const [pagination, setPagination] = React.useState({
    totalPages: 0,
    currentPage: 1,
    pageSize: 15,
  });
  const headers = [
    {
      label: t('Survey_Name', 'Survey Name'),
      key: 'name',
      sortable: true,
      render: Name,
    },
    {
      label: '',
      key: 'id',
      render: Selection,
      sortable: false,
    },
  ];
  const onQueryChange = (val) => {
    setQuery(val);
  };

  const matchingEntries =
    matchSorter(surveyEntries, query, {keys: ['name']}) ?? [];

  const paginatedEntries = React.useMemo(() => {
    return matchingEntries.slice(
      (pagination.currentPage - 1) * pagination.pageSize,
      pagination.currentPage * pagination.pageSize,
    );
  }, [pagination.currentPage, pagination.pageSize, matchingEntries]);

  React.useEffect(() => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      currentPage: 1,
      totalPages: Math.ceil(matchingEntries.length / prevPagination.pageSize),
    }));
  }, [matchingEntries.length]);

  return (
    <>
      <PanelHeader onCloseButtonClick={closePanel}>
        <>
          {typeof onBack === 'function' && (
            <Button onClick={onBack} type="ghost" iconLeftName="chevron-left" />
          )}
          <SubTitleLarge>
            {t('Select_a_Survey', 'Select a Survey')}
          </SubTitleLarge>
        </>
      </PanelHeader>
      <PanelBody>
        {loadingSurveySubmit ? (
          <div className={css.loaderContainer}>
            <Loading />
            <BodyLarge>
              {t(
                'Attaching_the_survey_you_selected_to_the_content',
                'Attaching the survey you selected to the content',
              )}
            </BodyLarge>
          </div>
        ) : (
          <div className={css.surveySelectionContent}>
            <TableTopBar>
              <DebouncedSearchInput
                classNames={{container: css.searchContainer}}
                value={query}
                onChange={onQueryChange}
                placeholder="Search for survey"
              />
            </TableTopBar>
            <Table
              borderRadius="0px"
              classNames={{wrapper: css.surveySelectionTableContainer}}
              isLoading={loading}
              headers={headers}
              entries={paginatedEntries}
              stickyHeader
              emptyText={
                <EmptyState
                  description={`${t(
                    'We_cant_find_any_items_matching_your_search',
                    'We cant find any items matching your search',
                  )}.`}
                  title={t('Sorry_No_result_found', 'Sorry! No result found')}
                  imageVariant="file"
                />
              }
              extras={{
                onSelect: onSubmit,
                surveyId,
              }}
            />
            <TableBottomBar>
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onChange={(page) => {
                  setPagination((pagination) => ({
                    ...pagination,
                    currentPage: page ?? 1,
                  }));
                }}
              >
                <SubTitleExtraSmall color="secondary">
                  {`${t('Showing_page', 'Showing page')} `}
                  {pagination.currentPage}
                  {` ${t('of', 'of')}`} {pagination.totalPages}
                </SubTitleExtraSmall>
              </Pagination>
            </TableBottomBar>
          </div>
        )}
      </PanelBody>
    </>
  );
};
